// src/components/appSettings/Banner/BannerDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiEdit2,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiCalendar,
  FiMapPin,
  FiSmartphone,
  FiMonitor,
  FiTablet,
  FiTrendingUp,
  FiTarget,
  FiClock,
  FiInfo,
  FiArrowLeft,
} from "react-icons/fi";
import {
  fetchBannerByIdThunk,
  deleteBannerThunk,
  updateBannerStatusThunk,
  fetchBannerAnalyticsThunk,
} from "../../../store/thunks/appSettingsThunks";
import Button from "../../../components/common/Button";
import toast from "react-hot-toast";

const BannerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedBanner, bannerAnalytics, loading } = useSelector(
    (state) => state.appSettings
  );
  const { branches } = useSelector((state) => state.branch);

  const [activeTab, setActiveTab] = useState("overview");
  const [previewDevice, setPreviewDevice] = useState("desktop");

  useEffect(() => {
    if (id) {
      dispatch(fetchBannerByIdThunk(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (activeTab === "analytics" && id) {
      dispatch(fetchBannerAnalyticsThunk(id));
    }
  }, [activeTab, id, dispatch]);

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this banner? This action cannot be undone."
      )
    ) {
      try {
        await dispatch(deleteBannerThunk(id)).unwrap();
        toast.success("Banner deleted successfully");
        navigate("/app-settings/banners");
      } catch (error) {
        toast.error(error.message || "Failed to delete banner");
      }
    }
  };

  const handleToggleStatus = async () => {
    try {
      await dispatch(
        updateBannerStatusThunk({
          id,
          statusData: { isActive: !selectedBanner.isActive },
        })
      ).unwrap();
      toast.success(
        `Banner ${
          selectedBanner.isActive ? "deactivated" : "activated"
        } successfully`
      );
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  const getBranchName = (branchCode) => {
    const branch = branches?.find((b) => b.code === branchCode);
    return branch ? `${branch.name} (${branchCode})` : branchCode;
  };

  const getStatusInfo = () => {
    if (!selectedBanner) return { text: "Unknown", color: "gray" };

    const now = new Date();
    const validFrom = new Date(selectedBanner.validFrom);
    const validUntil = new Date(selectedBanner.validUntil);

    if (!selectedBanner.isActive) {
      return { text: "Inactive", color: "gray" };
    }
    if (now < validFrom) {
      return { text: "Scheduled", color: "yellow" };
    }
    if (now > validUntil) {
      return { text: "Expired", color: "red" };
    }
    return { text: "Active", color: "green" };
  };

  if (loading || !selectedBanner) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statusInfo = getStatusInfo();

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/app-settings/banners")}
              >
                <FiArrowLeft className="mr-2" />
                Back to Banners
              </Button>
              <span
                className={`px-3 py-1 text-sm rounded-full bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}
              >
                {statusInfo.text}
              </span>
            </div>

            <h1 className="text-2xl font-bold text-gray-900">
              {selectedBanner.title}
            </h1>
            {selectedBanner.subtitle && (
              <p className="text-gray-600 mt-1">{selectedBanner.subtitle}</p>
            )}
            {selectedBanner.internalName && (
              <p className="text-sm text-gray-500 mt-1">
                Internal: {selectedBanner.internalName}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleToggleStatus}
              className={
                selectedBanner.isActive ? "text-red-600" : "text-green-600"
              }
            >
              {selectedBanner.isActive ? (
                <>
                  <FiEyeOff className="mr-2" />
                  Deactivate
                </>
              ) : (
                <>
                  <FiEye className="mr-2" />
                  Activate
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/app-settings/banners/edit/${id}`)}
            >
              <FiEdit2 className="mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              onClick={handleDelete}
              className="text-red-600 hover:bg-red-50"
            >
              <FiTrash2 className="mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b">
          <div className="flex space-x-8 px-6">
            {["overview", "preview", "analytics", "targeting", "schedule"].map(
              (tab) => (
                <button
                  key={tab}
                  className={`py-3 border-b-2 font-medium text-sm capitalize ${
                    activeTab === tab
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              )
            )}
          </div>
        </div>

        <div className="p-6">
          {activeTab === "overview" && (
            <OverviewTab
              banner={selectedBanner}
              getBranchName={getBranchName}
            />
          )}
          {activeTab === "preview" && (
            <PreviewTab
              banner={selectedBanner}
              previewDevice={previewDevice}
              setPreviewDevice={setPreviewDevice}
            />
          )}
          {activeTab === "analytics" && (
            <AnalyticsTab
              banner={selectedBanner}
              analytics={bannerAnalytics}
              loading={loading}
            />
          )}
          {activeTab === "targeting" && (
            <TargetingTab
              banner={selectedBanner}
              getBranchName={getBranchName}
            />
          )}
          {activeTab === "schedule" && <ScheduleTab banner={selectedBanner} />}
        </div>
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ banner, getBranchName }) => (
  <div className="space-y-6">
    {/* Quick Stats */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <StatCard
        icon={FiEye}
        label="Impressions"
        value={banner.analytics?.impressions || 0}
        color="blue"
      />
      <StatCard
        icon={FiTarget}
        label="Clicks"
        value={banner.analytics?.clicks || 0}
        color="green"
      />
      <StatCard
        icon={FiTrendingUp}
        label="CTR"
        value={
          banner.analytics?.impressions > 0
            ? `${(
                (banner.analytics.clicks / banner.analytics.impressions) *
                100
              ).toFixed(2)}%`
            : "0%"
        }
        color="purple"
      />
      <StatCard
        icon={FiSmartphone}
        label="Unique Users"
        value={banner.analytics?.uniqueUsers || 0}
        color="orange"
      />
    </div>

    {/* Basic Information */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <InfoSection title="Display Rules">
        <InfoRow label="Priority" value={banner.displayRules?.priority || 0} />
        <InfoRow label="Position" value={banner.displayRules?.position || 1} />
        <InfoRow
          label="Show Only Once"
          value={banner.displayRules?.showOnlyOnce ? "Yes" : "No"}
        />
        {banner.displayRules?.maxImpressions && (
          <InfoRow
            label="Max Impressions"
            value={banner.displayRules.maxImpressions}
          />
        )}
      </InfoSection>

      <InfoSection title="Action Configuration">
        <InfoRow label="Action Type" value={banner.action?.type || "NONE"} />
        {banner.action?.screen && (
          <InfoRow label="Screen" value={banner.action.screen} />
        )}
        {banner.action?.url && (
          <InfoRow label="URL" value={banner.action.url} />
        )}
        {banner.action?.deeplink && (
          <InfoRow label="Deep Link" value={banner.action.deeplink} />
        )}
      </InfoSection>
    </div>

    {/* Validity Period */}
    <InfoSection title="Validity Period">
      <div className="grid grid-cols-2 gap-4">
        <InfoRow
          label="Valid From"
          value={new Date(banner.validFrom).toLocaleString()}
        />
        <InfoRow
          label="Valid Until"
          value={new Date(banner.validUntil).toLocaleString()}
        />
      </div>
    </InfoSection>

    {/* Additional Info */}
    {(banner.campaign || banner.notes || banner.tags?.length > 0) && (
      <InfoSection title="Additional Information">
        {banner.campaign && (
          <InfoRow label="Campaign" value={banner.campaign} />
        )}
        {banner.tags?.length > 0 && (
          <div className="flex items-start gap-2">
            <span className="text-sm text-gray-500 min-w-[100px]">Tags:</span>
            <div className="flex flex-wrap gap-2">
              {banner.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
        {banner.notes && (
          <div className="flex items-start gap-2">
            <span className="text-sm text-gray-500 min-w-[100px]">Notes:</span>
            <p className="text-sm text-gray-900">{banner.notes}</p>
          </div>
        )}
      </InfoSection>
    )}
  </div>
);

// Preview Tab
const PreviewTab = ({ banner, previewDevice, setPreviewDevice }) => {
  const deviceOptions = [
    { value: "desktop", label: "Desktop", icon: FiMonitor },
    { value: "mobile", label: "Mobile", icon: FiSmartphone },
    { value: "tablet", label: "Tablet", icon: FiTablet },
  ];

  const getDeviceImage = () => {
    switch (previewDevice) {
      case "desktop":
        return banner.images?.desktop;
      case "mobile":
        return banner.images?.mobile;
      case "tablet":
        return banner.images?.tablet || banner.images?.desktop;
      default:
        return banner.images?.desktop;
    }
  };

  return (
    <div className="space-y-6">
      {/* Device Selector */}
      <div className="flex justify-center gap-4">
        {deviceOptions.map((device) => {
          const Icon = device.icon;
          return (
            <button
              key={device.value}
              onClick={() => setPreviewDevice(device.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                previewDevice === device.value
                  ? "border-primary bg-primary text-white"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            >
              <Icon />
              <span>{device.label}</span>
            </button>
          );
        })}
      </div>

      {/* Preview Container */}
      <div className="flex justify-center">
        <div
          className={`${
            previewDevice === "desktop"
              ? "w-full max-w-4xl"
              : previewDevice === "tablet"
              ? "w-full max-w-2xl"
              : "w-full max-w-md"
          }`}
        >
          <div className="bg-gray-100 rounded-lg p-4 shadow-lg">
            {getDeviceImage() ? (
              <img
                src={getDeviceImage()}
                alt={banner.title}
                className="w-full rounded-lg shadow-md"
              />
            ) : (
              <div className="bg-gray-300 rounded-lg h-64 flex items-center justify-center">
                <p className="text-gray-500">
                  No image available for {previewDevice}
                </p>
              </div>
            )}
          </div>

          {/* Banner Info Overlay */}
          <div className="mt-4 p-4 bg-white rounded-lg shadow">
            <h3 className="font-semibold text-lg">{banner.title}</h3>
            {banner.subtitle && (
              <p className="text-gray-600 text-sm mt-1">{banner.subtitle}</p>
            )}
            {banner.action?.type !== "NONE" && (
              <div className="mt-2 text-sm text-gray-500">
                Action: {banner.action.type}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image URLs */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium mb-2">Image URLs</h4>
        <div className="space-y-2 text-sm">
          {banner.images?.desktop && (
            <div>
              <span className="text-gray-500">Desktop: </span>
              <a
                href={banner.images.desktop}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                View Image
              </a>
            </div>
          )}
          {banner.images?.mobile && (
            <div>
              <span className="text-gray-500">Mobile: </span>
              <a
                href={banner.images.mobile}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                View Image
              </a>
            </div>
          )}
          {banner.images?.tablet && (
            <div>
              <span className="text-gray-500">Tablet: </span>
              <a
                href={banner.images.tablet}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                View Image
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Analytics Tab
const AnalyticsTab = ({ banner, analytics, loading }) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const data = analytics || banner.analytics || {};

  const ctr =
    data.impressions > 0
      ? ((data.clicks / data.impressions) * 100).toFixed(2)
      : 0;

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total Impressions"
          value={data.impressions || 0}
          icon={FiEye}
          color="blue"
        />
        <MetricCard
          label="Total Clicks"
          value={data.clicks || 0}
          icon={FiTarget}
          color="green"
        />
        <MetricCard
          label="Click-Through Rate"
          value={`${ctr}%`}
          icon={FiTrendingUp}
          color="purple"
        />
        <MetricCard
          label="Unique Users"
          value={data.uniqueUsers || 0}
          icon={FiSmartphone}
          color="orange"
        />
      </div>

      {/* Conversion Rate */}
      {data.conversionRate !== undefined && (
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-900 mb-2">
            Conversion Rate
          </h3>
          <p className="text-3xl font-bold text-green-700">
            {data.conversionRate.toFixed(2)}%
          </p>
        </div>
      )}

      {/* Last Viewed */}
      {data.lastViewed && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-gray-700">
            <FiClock />
            <span className="text-sm">
              Last viewed: {new Date(data.lastViewed).toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!data.impressions && !data.clicks && (
        <div className="text-center py-12">
          <FiInfo className="mx-auto text-4xl text-gray-400 mb-2" />
          <p className="text-gray-500">
            No analytics data available yet. Data will appear once users
            interact with this banner.
          </p>
        </div>
      )}
    </div>
  );
};

// Targeting Tab
const TargetingTab = ({ banner, getBranchName }) => (
  <div className="space-y-6">
    <InfoSection title="Branch Targeting">
      {banner.targeting?.branches?.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {banner.targeting.branches.map((branchCode) => (
            <span
              key={branchCode}
              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
            >
              {getBranchName(branchCode)}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-sm">All branches</p>
      )}
    </InfoSection>

    <InfoSection title="Device Targeting">
      <div className="flex flex-wrap gap-2">
        {banner.targeting?.deviceTypes?.map((device) => (
          <span
            key={device}
            className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
          >
            {device}
          </span>
        ))}
      </div>
    </InfoSection>

    {(banner.targeting?.minAppVersion || banner.targeting?.maxAppVersion) && (
      <InfoSection title="App Version Targeting">
        <div className="grid grid-cols-2 gap-4">
          {banner.targeting.minAppVersion && (
            <InfoRow
              label="Minimum Version"
              value={banner.targeting.minAppVersion}
            />
          )}
          {banner.targeting.maxAppVersion && (
            <InfoRow
              label="Maximum Version"
              value={banner.targeting.maxAppVersion}
            />
          )}
        </div>
      </InfoSection>
    )}

    {banner.targeting?.regions?.length > 0 && (
      <InfoSection title="Region Targeting">
        <div className="flex flex-wrap gap-2">
          {banner.targeting.regions.map((region) => (
            <span
              key={region}
              className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
            >
              {region}
            </span>
          ))}
        </div>
      </InfoSection>
    )}

    {banner.targeting?.customerGroups?.length > 0 && (
      <InfoSection title="Customer Group Targeting">
        <div className="flex flex-wrap gap-2">
          {banner.targeting.customerGroups.map((group) => (
            <span
              key={group}
              className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm"
            >
              {group}
            </span>
          ))}
        </div>
      </InfoSection>
    )}
  </div>
);

// Schedule Tab
const ScheduleTab = ({ banner }) => (
  <div className="space-y-6">
    <InfoSection title="Validity Period">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiCalendar className="text-green-700" />
            <span className="text-sm font-medium text-green-900">
              Start Date
            </span>
          </div>
          <p className="text-lg font-semibold text-green-700">
            {new Date(banner.validFrom).toLocaleString()}
          </p>
        </div>

        <div className="bg-red-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiCalendar className="text-red-700" />
            <span className="text-sm font-medium text-red-900">End Date</span>
          </div>
          <p className="text-lg font-semibold text-red-700">
            {new Date(banner.validUntil).toLocaleString()}
          </p>
        </div>
      </div>
    </InfoSection>

    {banner.displayRules?.daysOfWeek?.length > 0 && (
      <InfoSection title="Days of Week">
        <div className="flex flex-wrap gap-2">
          {banner.displayRules.daysOfWeek.map((day) => {
            const dayNames = [
              "Sunday",
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
            ];
            return (
              <span
                key={day}
                className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
              >
                {dayNames[day]}
              </span>
            );
          })}
        </div>
      </InfoSection>
    )}

    {banner.displayRules?.timeSlots?.length > 0 && (
      <InfoSection title="Time Slots">
        <div className="space-y-2">
          {banner.displayRules.timeSlots.map((slot, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-gray-50 rounded p-2"
            >
              <FiClock className="text-gray-600" />
              <span className="text-sm">
                {slot.startTime} - {slot.endTime}
              </span>
            </div>
          ))}
        </div>
      </InfoSection>
    )}

    {banner.displayRules?.excludeDates?.length > 0 && (
      <InfoSection title="Excluded Dates">
        <div className="flex flex-wrap gap-2">
          {banner.displayRules.excludeDates.map((date, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm"
            >
              {new Date(date).toLocaleDateString()}
            </span>
          ))}
        </div>
      </InfoSection>
    )}

    {/* Duration Info */}
    <div className="bg-blue-50 rounded-lg p-4">
      <h4 className="font-medium text-blue-900 mb-2">Campaign Duration</h4>
      <p className="text-sm text-blue-700">
        {Math.ceil(
          (new Date(banner.validUntil) - new Date(banner.validFrom)) /
            (1000 * 60 * 60 * 24)
        )}{" "}
        days
      </p>
    </div>
  </div>
);

// Helper Components
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div
    className={`bg-gradient-to-br from-${color}-50 to-${color}-100 rounded-lg p-4`}
  >
    <div className="flex items-center justify-between mb-2">
      <Icon className={`text-2xl text-${color}-600`} />
    </div>
    <p className="text-sm text-gray-600">{label}</p>
    <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
  </div>
);

const MetricCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white border rounded-lg p-4">
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`text-${color}-600`} />
      <span className="text-sm text-gray-600">{label}</span>
    </div>
    <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
  </div>
);

const InfoSection = ({ title, children }) => (
  <div className="bg-white border rounded-lg p-4">
    <h3 className="font-medium text-gray-900 mb-3">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-gray-500">{label}:</span>
    <span className="text-sm font-medium text-gray-900">{value}</span>
  </div>
);

export default BannerDetails;
